"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import Link from "@/components/Link";
import Image from "next/image";
import axios from "axios";
import { AppRoutes } from "@/lib/routeResolver";
import PackageCard from "@/app/(main)/components/PackageCard";
import type { HomePackage } from "@/lib/navigation";
import type {
  CruiseScope,
  CruiseTypeOption,
  CruiseShipOption,
  CruisePackageItem,
} from "@/lib/cruises";

// ─── Types ───────────────────────────────────────────────────────────

export interface ShipCard {
  shipId: string;
  cruise: {
    id: string;
    cruiseShipName: string;
    pageUrl: string;
    photos?: { singleURL?: string; url?: string };
  } | null;
}

interface Props {
  scope: CruiseScope;
  /** Clean canonical path (no query), e.g. "/cruises/Mediterranean/Italy". */
  basePath: string;
  name: string;
  cruiseTypes: CruiseTypeOption[];
  /** Server-validated selected type id ("" = All). */
  selectedCruiseTypeId: string;
  ships: CruiseShipOption[];
  /** Server-validated selected ship id ("" = Show All). */
  selectedShipId: string;
  shipCards: ShipCard[];
  initialPackages: CruisePackageItem[];
  initialTotalPages: number;
  initialTotalCount: number;
  sidebarExtra?: ReactNode;
}

const PAGE_SIZE = 10;

// ─── API client (CompanyId injected like lib/api.ts) ─────────────────

const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_ACCESS_TOKEN}`,
  },
  params: { CompanyId: process.env.NEXT_PUBLIC_COMPANY_ID },
});

const BRAND_ID = process.env.NEXT_PUBLIC_BRAND_ID_CENTRAL_HOLIDAYS;

/** RegionId | CountryId for the packages endpoint. */
function packageScopeParams(scope: CruiseScope): Record<string, string> {
  return scope.kind === "region"
    ? { RegionId: scope.id }
    : { CountryId: scope.id };
}

/** regionId | destionationId[sic] for the used_* endpoints. */
function usedScopeParams(scope: CruiseScope): Record<string, string> {
  return scope.kind === "region"
    ? { regionId: scope.id }
    : { destionationId: scope.id };
}

// ─── Map CruisePackageItem → HomePackage for the shared PackageCard ──

function toHomePackage(pack: CruisePackageItem): HomePackage {
  const cities =
    pack.packageRegions
      ?.flatMap((r) => r.cityDays ?? [])
      .map((c) => c.cityName)
      .filter(Boolean) ?? [];

  return {
    id: pack.id,
    pageURL: pack.pageURL,
    packageName: pack.packageName,
    photoURL: pack.photoGallery?.[0]?.url || "/main/img/tour/tour1.jpg",
    startingPrice: Number(pack.rate?.startingFrom ?? 0),
    cities,
    days: pack.days ?? 0,
    nights: pack.nights ?? 0,
    cruiseNights: pack.cruiseNights ?? 0,
    packageTypeName:
      pack.packageTypeName ?? (pack.days > 0 ? `${pack.days} Days` : ""),
    isNew: pack.isNew === true,
  };
}

// ─── Skeleton + pagination (shared style with CruisePackagesClient) ──

function PackageCardSkeleton() {
  return (
    <div className="col-md-6">
      <div
        className="tour-box th-ani h-100"
        style={{ overflow: "hidden", borderRadius: 8 }}
      >
        <div
          className="skeleton-pulse"
          style={{ height: 250, background: "#e9ecef" }}
        />
        <div className="tour-content" style={{ padding: "16px" }}>
          <div
            className="skeleton-pulse mb-3"
            style={{ height: 20, width: "85%", background: "#e9ecef", borderRadius: 4 }}
          />
          <div
            className="skeleton-pulse mb-2"
            style={{ height: 12, width: "60%", background: "#e9ecef", borderRadius: 4 }}
          />
        </div>
      </div>
    </div>
  );
}

function PaginationArrows({
  currentPage,
  totalPages,
  totalCount,
  loading,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1 && totalCount <= 0) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);

  return (
    <div className="d-flex align-items-center justify-content-between mb-4">
      <p className="blog-text mb-0">
        Showing{" "}
        <strong>
          {startItem}–{endItem}
        </strong>{" "}
        of <strong>{totalCount}</strong> packages
      </p>
      {totalPages > 1 && (
        <div className="d-flex gap-2">
          <button
            className="th-btn style4"
            disabled={currentPage <= 1 || loading}
            onClick={() => onPageChange(currentPage - 1)}
            style={{ padding: "4px 10px" }}
          >
            <i className="fa fa-arrow-left"></i> Prev
          </button>
          <button
            className="th-btn style4"
            disabled={currentPage >= totalPages || loading}
            onClick={() => onPageChange(currentPage + 1)}
            style={{ padding: "4px 10px" }}
          >
            Next <i className="fa fa-arrow-right"></i>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────

export default function CruiseListingClient({
  scope,
  basePath,
  name,
  cruiseTypes,
  selectedCruiseTypeId,
  ships: initialShips,
  selectedShipId: initialSelectedShipId,
  shipCards: initialShipCards,
  initialPackages,
  initialTotalPages,
  initialTotalCount,
  sidebarExtra,
}: Props) {
  const [packages, setPackages] = useState(initialPackages);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTypeId, setSelectedTypeId] = useState(selectedCruiseTypeId);
  const [ships, setShips] = useState(initialShips);
  const [shipCards, setShipCards] = useState(initialShipCards);
  const [selectedShipId, setSelectedShipId] = useState(initialSelectedShipId);
  const [isLoading, setIsLoading] = useState(false);

  const gridRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  // Cruise types only act as a filter when there is real choice (>1). With a
  // single type, the scope's unfiltered set already equals it, so we apply no
  // filter and add no query param (keeps the URL canonical-equal).
  const multiType = cruiseTypes.length > 1;

  const typeName = useCallback(
    (id: string) => cruiseTypes.find((t) => t.id === id)?.name ?? "",
    [cruiseTypes],
  );

  // ── URL sync (shallow, via History API — no server round-trip) ──
  // The clean `basePath` stays the canonical; only `type`/`ship` query params
  // move. Unrelated params (e.g. utm) are preserved.
  const syncUrl = useCallback(
    (
      typeNameVal: string,
      shipNameVal: string,
      mode: "push" | "replace",
    ) => {
      if (typeof window === "undefined") return;
      const sp = new URLSearchParams(window.location.search);
      sp.delete("type");
      sp.delete("ship");
      if (typeNameVal) sp.set("type", typeNameVal);
      if (shipNameVal) sp.set("ship", shipNameVal);
      const qs = sp.toString();
      const url = `${basePath}${qs ? `?${qs}` : ""}`;
      const current = `${window.location.pathname}${window.location.search}`;
      if (url === current && mode === "replace") return;
      window.history[mode === "push" ? "pushState" : "replaceState"](
        null,
        "",
        url,
      );
    },
    [basePath],
  );

  function scrollToGrid() {
    if (gridRef.current) {
      const y =
        gridRef.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }

  // ── Fetchers ──
  const fetchPackages = useCallback(
    async (page: number, typeNameVal: string, shipId: string) => {
      const res = await apiClient.get("inventory/package", {
        params: {
          PageSize: PAGE_SIZE,
          PageNumber: page,
          IsWebsite: true,
          OnlyWebsiteShownCruises: true,
          // Match the scoped destination against the package's MAIN destination
          // only, so a country page shows packages that are truly about that
          // country — not ones where it's merely an itinerary stop point.
          IsMainDestination: true,
          BrandId: BRAND_ID,
          ...packageScopeParams(scope),
          ...(typeNameVal ? { CruiseTypeName: typeNameVal } : {}),
          ...(shipId ? { CruiseShipId: shipId } : {}),
        },
      });
      return {
        packages: (res.data?.data ?? []) as CruisePackageItem[],
        totalPages: (res.data?.totalPages ?? 0) as number,
        count: (res.data?.count ?? 0) as number,
      };
    },
    [scope],
  );

  const fetchShips = useCallback(
    async (cruiseTypeId?: string): Promise<CruiseShipOption[]> => {
      try {
        const res = await apiClient.get("inventory/package/used_cruise_ships", {
          params: {
            ...usedScopeParams(scope),
            ...(cruiseTypeId ? { cruiseTypeId } : {}),
          },
        });
        return (res.data ?? []) as CruiseShipOption[];
      } catch {
        return [];
      }
    },
    [scope],
  );

  const fetchShipCards = useCallback(
    async (list: CruiseShipOption[]): Promise<ShipCard[]> => {
      return Promise.all(
        list.map(async (ship) => {
          try {
            const { data } = await apiClient.get(
              "inventory/cruise/bycruiseshipid",
              { params: { CruiseShipId: ship.id } },
            );
            return { shipId: ship.id, cruise: data?.data ?? null };
          } catch {
            return { shipId: ship.id, cruise: null };
          }
        }),
      );
    },
    [],
  );

  // ── Selecting a cruise type: re-fetch ships + their cards, reset ship,
  //    re-fetch packages (page 1). `desiredShipName` lets back/forward restore
  //    a ship selection after the ship list reloads. ──
  const selectType = useCallback(
    async (
      typeId: string,
      opts: { history?: "push" | "replace" | "none"; desiredShipName?: string } = {},
    ) => {
      const { history = "push", desiredShipName } = opts;
      setIsLoading(true);
      shouldScrollRef.current = true;

      const effTypeName = multiType && typeId ? typeName(typeId) : "";
      const cruiseTypeId = multiType && typeId ? typeId : undefined;

      const newShips = await fetchShips(cruiseTypeId);

      // Default ship selection for the new list (single → that one; else All).
      let nextShipId = newShips.length === 1 ? newShips[0].id : "";
      if (desiredShipName && newShips.length > 1) {
        const match = newShips.find((s) => s.shipName === desiredShipName);
        nextShipId = match ? match.id : "";
      }
      const shipFilter = newShips.length > 1 && nextShipId ? nextShipId : "";
      const shipNameVal =
        newShips.length > 1 && nextShipId
          ? (newShips.find((s) => s.id === nextShipId)?.shipName ?? "")
          : "";

      const [pkg, cards] = await Promise.all([
        fetchPackages(1, effTypeName, shipFilter),
        fetchShipCards(newShips),
      ]);

      setSelectedTypeId(typeId);
      setShips(newShips);
      setShipCards(cards);
      setSelectedShipId(nextShipId);
      setCurrentPage(1);
      setPackages(pkg.packages);
      setTotalPages(pkg.totalPages);
      setTotalCount(pkg.count);
      setIsLoading(false);

      if (history !== "none") syncUrl(effTypeName, shipNameVal, history);
      requestAnimationFrame(scrollToGrid);
    },
    [multiType, typeName, fetchShips, fetchPackages, fetchShipCards, syncUrl],
  );

  // ── Selecting a ship: re-fetch packages only (page 1). ──
  const selectShip = useCallback(
    async (shipId: string, history: "push" | "replace" | "none" = "push") => {
      setIsLoading(true);
      shouldScrollRef.current = true;

      const effTypeName = multiType && selectedTypeId ? typeName(selectedTypeId) : "";
      const shipFilter = ships.length > 1 && shipId ? shipId : "";
      const shipNameVal =
        ships.length > 1 && shipId
          ? (ships.find((s) => s.id === shipId)?.shipName ?? "")
          : "";

      const pkg = await fetchPackages(1, effTypeName, shipFilter);

      setSelectedShipId(shipId);
      setCurrentPage(1);
      setPackages(pkg.packages);
      setTotalPages(pkg.totalPages);
      setTotalCount(pkg.count);
      setIsLoading(false);

      if (history !== "none") syncUrl(effTypeName, shipNameVal, history);
      requestAnimationFrame(scrollToGrid);
    },
    [multiType, selectedTypeId, typeName, ships, fetchPackages, syncUrl],
  );

  // ── Pagination (client-only; page is not part of the canonical URL). ──
  const goToPage = useCallback(
    async (page: number) => {
      setIsLoading(true);
      shouldScrollRef.current = true;
      const effTypeName = multiType && selectedTypeId ? typeName(selectedTypeId) : "";
      const shipFilter = ships.length > 1 && selectedShipId ? selectedShipId : "";
      const pkg = await fetchPackages(page, effTypeName, shipFilter);
      setCurrentPage(page);
      setPackages(pkg.packages);
      setTotalPages(pkg.totalPages);
      setTotalCount(pkg.count);
      setIsLoading(false);
      requestAnimationFrame(scrollToGrid);
    },
    [multiType, selectedTypeId, typeName, ships, selectedShipId, fetchPackages],
  );

  // ── On mount: reconcile the URL to the server-validated selection. This
  //    strips stale/non-matching `type`/`ship` params (and single-item params)
  //    without a redirect, while preserving unrelated params. ──
  useEffect(() => {
    const effTypeName =
      multiType && selectedCruiseTypeId ? typeName(selectedCruiseTypeId) : "";
    const shipNameVal =
      initialShips.length > 1 && initialSelectedShipId
        ? (initialShips.find((s) => s.id === initialSelectedShipId)?.shipName ?? "")
        : "";
    syncUrl(effTypeName, shipNameVal, "replace");
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Back/forward: restore filter state from the URL. ──
  useEffect(() => {
    function onPop() {
      const sp = new URLSearchParams(window.location.search);
      const tName = sp.get("type") || "";
      const sName = sp.get("ship") || "";
      const typeId = multiType
        ? (cruiseTypes.find((t) => t.name === tName)?.id ?? "")
        : (cruiseTypes[0]?.id ?? "");
      // Re-run the full type → ship → packages chain, restoring the ship from
      // the URL; do not push another history entry.
      selectType(typeId, { history: "none", desiredShipName: sName });
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [multiType, cruiseTypes, selectType]);

  const sortedPackages = [...packages].sort(
    (a, b) => (a.websiteOrder ?? 999) - (b.websiteOrder ?? 999),
  );

  const selectedShipName = ships.find((s) => s.id === selectedShipId)?.shipName;

  return (
    <div className="row" ref={gridRef}>
      {/* ── Left: Package Cards ── */}
      <div className="col-xxl-8 col-lg-7">
        <div className="page-single">
          <div className="page-content d-block mb-4">
            <div className="page-meta mt-0 mb-20">
              <span className="page-tag">{name} Cruises</span>
              <span className="ratting ms-2">
                {isLoading
                  ? "Loading..."
                  : `${totalCount} Package${totalCount !== 1 ? "s" : ""}`}
                {selectedShipName ? ` · ${selectedShipName}` : ""}
              </span>
            </div>
            <h2 className="box-title mb-2">
              {selectedShipName
                ? `${selectedShipName} Packages`
                : `Explore ${name} Cruises`}
            </h2>
            <p className="blog-text">
              Discover our curated collection of {name} cruise packages. Choose
              from a variety of itineraries and cruise ships for an
              unforgettable voyage.
            </p>
          </div>

          {!isLoading && totalCount > 0 && (
            <PaginationArrows
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              loading={isLoading}
              onPageChange={goToPage}
            />
          )}

          <div
            className="row gy-4 gx-4"
            style={{
              opacity: isLoading ? 0.3 : 1,
              transition: "opacity 0.3s ease-in-out",
              minHeight: 200,
            }}
          >
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <PackageCardSkeleton key={i} />
              ))
            ) : sortedPackages.length > 0 ? (
              sortedPackages.map((pack) => (
                <div key={pack.id} className="col-md-6">
                  <PackageCard pack={toHomePackage(pack)} />
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <div className="mb-4">
                  <i className="fa-light fa-ship fa-4x text-theme"></i>
                </div>
                <p className="blog-text mb-4">
                  No cruise packages found
                  {selectedShipName ? ` for ${selectedShipName}` : ""} in {name}.
                </p>
                {selectedShipId && (
                  <button
                    className="th-btn style1 th-icon"
                    onClick={() => selectShip("")}
                  >
                    View All Packages
                  </button>
                )}
              </div>
            )}
          </div>

          {!isLoading && totalPages > 1 && (
            <div className="mt-40">
              <PaginationArrows
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                loading={isLoading}
                onPageChange={goToPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Right: Sidebar ── */}
      <div className="col-xxl-4 col-lg-5">
        <aside
          className="sidebar-area style3 position-sticky"
          style={{ top: 100 }}
        >
          {/* 1) Filter by Cruise Type */}
          {cruiseTypes.length > 0 && (
            <div className="widget widget_categories">
              <h3 className="widget_title">Filter by Cruise Type</h3>
              <ul>
                {/* "All" only when there is real choice (>1 type). */}
                {multiType && (
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading) selectType("");
                      }}
                      className={`pe-0 ${!selectedTypeId ? "fw-bold text-theme" : ""}`}
                      title="Show all cruise types"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img width="25" src="/main/img/theme-img/worldwide.svg" alt="All Cruise Types" />
                      All Cruise Types
                      {!selectedTypeId && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                )}
                {cruiseTypes.map((ct) => (
                  <li key={ct.id}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading && (multiType || ct.id !== selectedTypeId))
                          selectType(ct.id);
                      }}
                      className={`pe-0 ${selectedTypeId === ct.id ? "text-theme fw-bold" : ""}`}
                      title={ct.name}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img width="25" src="/main/img/theme-img/route.svg" alt={ct.name} />
                      {ct.name}
                      {selectedTypeId === ct.id && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 2) Filter by Cruise Ship */}
          {ships.length > 0 && (
            <div className="widget widget_categories">
              <h3 className="widget_title">Filter by Cruise Ship</h3>
              <ul>
                {ships.length > 1 && (
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading) selectShip("");
                      }}
                      className={`pe-0 ${!selectedShipId ? "fw-bold text-theme" : ""}`}
                      title="Show all cruise ships"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img width="25" src="/main/img/theme-img/worldwide.svg" alt="Show All Ships" />
                      Show All
                      {!selectedShipId && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                )}
                {ships.map((ship) => (
                  <li key={ship.id}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isLoading && (ships.length > 1 || ship.id !== selectedShipId))
                          selectShip(ship.id);
                      }}
                      className={`pe-0 ${selectedShipId === ship.id ? "text-theme fw-bold" : ""}`}
                      title={ship.shipName}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img width="25" src="/main/img/theme-img/route.svg" alt={ship.shipName} />
                      {ship.shipName}
                      {selectedShipId === ship.id && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 3) Cruise Ships showcase (image cards) */}
          {shipCards.some((c) => c.cruise) && (
            <div className="widget">
              <h3 className="widget_title">Cruise Ships</h3>
              <div className="recent-post-wrap">
                {shipCards.map(
                  (item) =>
                    item.cruise && (
                      <div className="recent-post" key={item.shipId}>
                        <div
                          className="media-img"
                          style={{
                            minWidth: 80,
                            maxWidth: 80,
                            height: 70,
                            borderRadius: 6,
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <Link
                            href={AppRoutes.cruiseShip(item.cruise.pageUrl)}
                            title={item.cruise.cruiseShipName}
                          >
                            <Image
                              src={
                                item.cruise.photos?.singleURL ||
                                item.cruise.photos?.url ||
                                "/main/img/blank.png"
                              }
                              alt={item.cruise.cruiseShipName}
                              fill
                              style={{ objectFit: "cover" }}
                              unoptimized
                            />
                          </Link>
                        </div>
                        <div className="media-body">
                          <h4 className="post-title">
                            <Link
                              href={AppRoutes.cruiseShip(item.cruise.pageUrl)}
                              title={item.cruise.cruiseShipName}
                            >
                              {item.cruise.cruiseShipName}
                            </Link>
                          </h4>
                          <div className="recent-post-meta">
                            <Link
                              href={AppRoutes.cruiseShip(item.cruise.pageUrl)}
                              title={`View ${item.cruise.cruiseShipName}`}
                            >
                              <i className="fa-light fa-ship me-1"></i>
                              View Ship
                            </Link>
                          </div>
                        </div>
                      </div>
                    ),
                )}
              </div>
            </div>
          )}

          {sidebarExtra}
        </aside>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes skeletonPulse {
              0% { opacity: 0.6; }
              50% { opacity: 1; }
              100% { opacity: 0.6; }
            }
            .skeleton-pulse { animation: skeletonPulse 1.5s ease-in-out infinite; }
          `,
        }}
      />
    </div>
  );
}
